import {
  Examination_Type,
  ExaminationHeaderQuery,
  Trial_Status,
  TrialListQuery,
  useAddCompanyCandidatesToExaminationMutation,
  useCancelExaminationMutation,
} from "@generated/graphql";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import AddCandidateModal, { AddCandidateModalProps } from "components/AddCandidateModal";
import ExaminationTypeTitle from "components/ExaminationTypeTitle";
import { TESTING_TYPES_DATA } from "constants/global";
import { ADD_CANDIDATES_URL } from "constants/urls";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import useIsCreate from "hooks/useIsCreate";
import useSetTabTitle from "hooks/useSetTabTitle";
import useUploadCandidates from "hooks/useUploadCandidates";
import { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import CancelExaminationButton from "../../components/CancelExaminationButton";
import { getTestingTitle } from "../../helpers";
import { ExaminationHeaderRefetch, TrialsListRefetch } from "../types";

interface HeaderProps {
  trialsData: TrialListQuery["trials"] | undefined;
  examination: ExaminationHeaderQuery["examination"] | undefined;
  examinationType: Examination_Type | undefined;
  trialsListRefetch: TrialsListRefetch;
  examinationHeaderRefetch: ExaminationHeaderRefetch;
}

const Header = ({
  trialsData,
  examination,
  examinationType,
  trialsListRefetch,
  examinationHeaderRefetch,
}: HeaderProps) => {
  const { addToast } = useContext(ToastContext);
  const isCreate = useIsCreate();
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [cancelExamination, { loading: cancelExaminationLoading }] = useCancelExaminationMutation();
  const [addCompanyCandidatesToExamination] = useAddCompanyCandidatesToExaminationMutation();

  const isCanCancel = examination?.isActive;

  const handleCancelExamination = async () => {
    cancelExamination({
      variables: {
        examinationId: id!,
      },
    })
      .then(() => {
        addToast({ type: ToastTypeEnum.SUCCESS, text: "Тестирование завершено" });
        trialsListRefetch();
        examinationHeaderRefetch();
      })
      .catch((e) => {
        parseApolloError(e, { addToast });
      });
  };

  const { uploadCandidates, uploadCandidatesLoading } = useUploadCandidates({
    uploadUrl: (fileId) => `${ADD_CANDIDATES_URL}/${fileId}`,
  });

  const onSubmitAddCandidateModal: AddCandidateModalProps["onSubmit"] = async ({
    selected: companyCandidateIds,
    formData,
  }) => {
    if (companyCandidateIds.length) {
      addCompanyCandidatesToExamination({
        variables: {
          input: {
            // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
            examinationId: examination?.id!,
            companyCandidateIds,
          },
        },
      })
        .then(() => {
          addToast({ type: ToastTypeEnum.SUCCESS });
          trialsListRefetch();
        })
        .catch((e) => parseApolloError(e, { addToast }));
    }

    if (formData) {
      const res = await uploadCandidates({ ...formData, examinationId: id! });
      if (res instanceof Response && res.ok) {
        trialsListRefetch();
      }
    }
  };

  const title = getTestingTitle(examination);
  useSetTabTitle(title, examination?.numericId);

  const examinationTypeData = TESTING_TYPES_DATA.find((data) => data.type === examinationType);

  return (
    <Stack
      direction='row'
      justifyContent='space-between'
      mb={2.5}
      spacing={2}>
      <Stack
        direction='row'
        alignItems='flex-start'
        spacing={2}>
        <Typography variant='h28'>{title}</Typography>
        {Boolean(examinationTypeData) && <ExaminationTypeTitle examinationTypeData={examinationTypeData} />}
      </Stack>
      <Stack
        direction='row'
        spacing={2}
        alignItems='baseline'>
        {!isCreate && (
          <CancelExaminationButton
            buttonProps={{ disabled: !isCanCancel, loading: cancelExaminationLoading }}
            tooltipProps={{ title: !isCanCancel ? "Тестирование уже завершено" : "" }}
            withConfirmation={trialsData?.data?.some((trial) => trial.status === Trial_Status.Started)}
            handleCancelExamination={handleCancelExamination}
            confirmModalProps={{
              description:
                // eslint-disable-next-line max-len
                "В настоящее время тестирование проходят участники. При завершении тестирования их прохождение будет остановлено, а неотвеченные вопросы будут отмечены как «Не отвечено (завершено администратором)».",
            }}
          />
        )}
        {examinationType === Examination_Type.Group && (
          <>
            <Tooltip title={isCreate ? "Добавление кандидатов будет доступно после создания тестирования" : ""}>
              <Box>
                <Button
                  variant='contained'
                  color='primary'
                  disabled={isCreate}
                  onClick={() => setIsModalOpen(true)}
                  sx={{ whiteSpace: "nowrap" }}>
                  Добавить кандидата
                </Button>
              </Box>
            </Tooltip>
            {isModalOpen && (
              <AddCandidateModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={onSubmitAddCandidateModal}
                submitButtonProps={{ loading: uploadCandidatesLoading }}
                candidatesUploadButton
                withCandidatesList
              />
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default Header;
